import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  const doLogout = useCallback(() => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
  }, []);

  // Refresh token every 6 hours to prevent expiration
  const startRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    refreshTimerRef.current = setInterval(async () => {
      try {
        const res = await axios.post(`${API_URL}/api/auth/refresh`);
        const newToken = res.data.access_token;
        localStorage.setItem('token', newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      } catch (err) {
        // Token refresh failed, don't logout yet - let interceptor handle it
      }
    }, 6 * 60 * 60 * 1000); // 6 hours
  }, []);

  useEffect(() => {
    // Setup axios interceptor for 401 errors
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401 && token) {
          // Token expired or invalid - try refresh once
          const originalRequest = error.config;
          if (!originalRequest._retry) {
            originalRequest._retry = true;
            try {
              const res = await axios.post(`${API_URL}/api/auth/refresh`);
              const newToken = res.data.access_token;
              localStorage.setItem('token', newToken);
              axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              return axios(originalRequest);
            } catch (refreshErr) {
              // Refresh also failed - session truly expired
              doLogout();
              return Promise.reject(error);
            }
          }
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [token, doLogout]);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const response = await axios.get(`${API_URL}/api/auth/me`);
          setUser(response.data);
          startRefreshTimer();

          // Try to refresh token on page load (extends session on each visit)
          try {
            const refreshRes = await axios.post(`${API_URL}/api/auth/refresh`);
            const newToken = refreshRes.data.access_token;
            localStorage.setItem('token', newToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          } catch (_) { /* ignore refresh error, token still valid */ }
        } catch (error) {
          // Token invalid/expired - clear and redirect to login
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();

    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    startRefreshTimer();
    return userData;
  };

  const register = async (name, email, password, companyName = '') => {
    const response = await axios.post(`${API_URL}/api/auth/register`, { 
      name, 
      email, 
      password, 
      company_name: companyName || `Empresa de ${name}`
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    startRefreshTimer();
    return userData;
  };

  const logout = () => {
    doLogout();
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
